import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useCallback, useEffect } from "react";

import type { ProductsQuery } from "~/graphql/graphql-new.gen";
import {
  ProductsUpdatedDocument,
  type ProductsUpdatedSubscription,
  useProductsQuery,
} from "~/graphql/graphql-new.gen";

import { activeSaleIdState } from "../stores/sales";
import { useGraphQLSubscription } from "./use-graphql-subscription";

export const useProductsUpdatedSubscription = () => {
  const client = useGraphQLSubscription();
  const queryClient = useQueryClient();
  const activeSaleId = useAtomValue(activeSaleIdState);

  const updateProducts = useCallback(
    (productUpdated: ProductsUpdatedSubscription["productsUpdated"]) => {
      const oldArray = queryClient.getQueryData<ProductsQuery>(
        useProductsQuery.getKey({
          saleId: activeSaleId ?? "",
        }),
      );
      if (!oldArray?.products) return;

      const newArray = oldArray.products.map((p) => {
        const product = productUpdated.find((pr) => pr.id === p.id);
        if (!product) return p;
        return {
          ...p,
          variants: p.variants.map((v) => {
            const variant = product.variants.find((pr) => pr.id === v.id);
            if (!variant) return v;
            return {
              ...v,
              stock: variant.stock,
              stockAlertsSummary: variant.stockAlertsSummary,
            };
          }),
        };
      });

      queryClient.setQueryData(
        useProductsQuery.getKey({
          saleId: activeSaleId ?? "",
        }),
        {
          products: newArray,
        },
      );
    },
    [activeSaleId, queryClient],
  );

  useEffect(() => {
    const unsubscribe = client.subscribe<ProductsUpdatedSubscription>(
      {
        query: ProductsUpdatedDocument,
        variables: {
          saleId: activeSaleId,
        },
      },
      {
        next: (data) => {
          if (!activeSaleId) return;
          if (!data.data?.productsUpdated) return;
          updateProducts(data.data.productsUpdated);
        },
        error: (error) => {
          // TODO: Handle error
          console.error(error);
        },
        complete: () => {
          // Do nothing
        },
      },
    );

    return () => {
      unsubscribe();
    };
  }, [client, queryClient, activeSaleId, updateProducts]);
};
